import Config from './Config.js';
import Logger from './Logger.js';
export const NODE_ITEM_LEN = 8 * 4 + 8;
export const DEFAULT_NODE_SIZE = 16;
export function calcTreeSize(numItems, nodeSize) {
    nodeSize = Math.min(Math.max(+nodeSize, 2), 65535);
    let n = numItems;
    let numNodes = n;
    do {
        n = Math.ceil(n / nodeSize);
        numNodes += n;
    } while (n !== 1);
    return numNodes * NODE_ITEM_LEN;
}
export function generateLevelBounds(numItems, nodeSize) {
    if (nodeSize < 2)
        throw new Error('Node size must be at least 2');
    if (numItems === 0)
        throw new Error('Number of items must be greater than 0');
    let n = numItems;
    let numNodes = n;
    const levelNumNodes = [n];
    do {
        n = Math.ceil(n / nodeSize);
        numNodes += n;
        levelNumNodes.push(n);
    } while (n !== 1);
    const levelOffsets = [];
    n = numNodes;
    for (const size of levelNumNodes) {
        levelOffsets.push(n - size);
        n -= size;
    }
    levelOffsets.reverse();
    levelNumNodes.reverse();
    const levelBounds = [];
    for (let i = 0; i < levelNumNodes.length; i++)
        levelBounds.push([levelOffsets[i], levelOffsets[i] + levelNumNodes[i]]);
    levelBounds.reverse();
    return levelBounds;
}
export async function* streamSearch(numItems, nodeSize, rect, readNode) {
    class NodeRange {
        constructor(nodes, level) {
            this._level = level;
            this.nodes = nodes;
        }
        level() {
            return this._level;
        }
        startNode() {
            return this.nodes[0];
        }
        endNode() {
            return this.nodes[1];
        }
        extendEndNodeToNewOffset(newOffset) {
            console.assert(newOffset > this.nodes[1]);
            this.nodes[1] = newOffset;
        }
        toString() {
            return `[NodeRange level: ${this._level}, nodes: ${this.nodes[0]}-${this.nodes[1]}]`;
        }
    }
    const { minX, minY, maxX, maxY } = rect;
    Logger.info(`tree items: ${numItems}, nodeSize: ${nodeSize}`);
    const levelBounds = generateLevelBounds(numItems, nodeSize);
    const leafNodesOffset = levelBounds[0][0];
    const rootNodeRange = (() => {
        const range = [0, 1];
        const level = levelBounds.length - 1;
        return new NodeRange(range, level);
    })();
    const queue = [rootNodeRange];
    Logger.debug(`starting stream search with queue: ${queue}, numItems: ${numItems}, nodeSize: ${nodeSize}, levelBounds: ${levelBounds}`);
    while (queue.length != 0) {
        const nodeRange = queue.shift();
        Logger.debug(`popped node: ${nodeRange}, queueLength: ${queue.length}`);
        const nodeIndex = nodeRange.startNode();
        const isLeafNode = nodeIndex >= leafNodesOffset;
        const [, levelBound] = levelBounds[nodeRange.level()];
        const end = Math.min(nodeRange.endNode() + nodeSize, levelBound);
        const length = end - nodeIndex;
        const buffer = await readNode(nodeIndex * NODE_ITEM_LEN, length * NODE_ITEM_LEN);
        const float64Array = new Float64Array(buffer);
        const uint32Array = new Uint32Array(buffer);
        for (let pos = nodeIndex; pos < end; pos++) {
            const nodePos = (pos - nodeIndex) * 5;
            if (maxX < float64Array[nodePos + 0])
                continue;
            if (maxY < float64Array[nodePos + 1])
                continue;
            if (minX > float64Array[nodePos + 2])
                continue;
            if (minY > float64Array[nodePos + 3])
                continue;
            const low32Offset = uint32Array[(nodePos << 1) + 8];
            const high32Offset = uint32Array[(nodePos << 1) + 9];
            const offset = readUint52(high32Offset, low32Offset);
            if (isLeafNode) {
                const featureLength = (() => {
                    if (pos < numItems - 1) {
                        const nextPos = (pos - nodeIndex + 1) * 5;
                        const low32Offset = uint32Array[(nextPos << 1) + 8];
                        const high32Offset = uint32Array[(nextPos << 1) + 9];
                        const nextOffset = readUint52(high32Offset, low32Offset);
                        return nextOffset - offset;
                    }
                    else {
                        return null;
                    }
                })();
                yield [offset, pos - leafNodesOffset, featureLength];
                continue;
            }
            const extraRequestThresholdNodes = Config.global.extraRequestThreshold() / NODE_ITEM_LEN;
            const nearestNodeRange = queue[queue.length - 1];
            if (nearestNodeRange !== undefined &&
                nearestNodeRange.level() == nodeRange.level() - 1 &&
                offset < nearestNodeRange.endNode() + extraRequestThresholdNodes) {
                Logger.debug(`Merging "nodeRange" request into existing range: ${nearestNodeRange}, newOffset: ${nearestNodeRange.endNode()} -> ${offset}`);
                nearestNodeRange.extendEndNodeToNewOffset(offset);
                continue;
            }
            const newNodeRange = (() => {
                const level = nodeRange.level() - 1;
                const range = [offset, offset + 1];
                return new NodeRange(range, level);
            })();
            if (nearestNodeRange !== undefined &&
                nearestNodeRange.level() == newNodeRange.level()) {
                Logger.info(`Same level, but too far away. Pushing new request at offset: ${offset} rather than merging with distant ${nearestNodeRange}`);
            }
            else {
                Logger.info(`Pushing new level for ${newNodeRange} onto queue with nearestNodeRange: ${nearestNodeRange} since there's not already a range for this level.`);
            }
            queue.push(newNodeRange);
        }
    }
}
function readUint52(high32Bits, low32Bits) {
    if ((high32Bits & 0xfff00000) != 0) {
        throw Error('integer is too large to be safely represented');
    }
    const result = low32Bits + high32Bits * 2 ** 32;
    return result;
}
//# sourceMappingURL=packedrtree.js.map